// SmoothLife — a continuous-domain generalization of Conway's Game of Life
// (Rafler 2011), run as a WebGL fragment-shader cellular automaton.
//
// The state is a single scalar field f in [0,1] stored in a render target.
// Each step samples a neighborhood (inner disk + outer annulus), computes the
// two local averages (m = inner fill, n = outer fill), and feeds them through
// a stack of sigmoids to decide birth/death. We ping-pong between two float
// targets so each frame reads the previous state and writes the next.
//
// Returned to the canvas manager as { render, dispose } so this sketch owns
// its multi-pass render loop instead of the manager's single render call.
//
// This is the reference example for the shared kit: seed / step / display are
// all fullscreen passes on `stage`, the two float targets come from
// `createPingPong`, and the color language reuses GLSL.palette. What's unique to
// SmoothLife is only the seed noise and the SIM_FRAG rule below.

import * as THREE from "three";
import { createStage, createPingPong, GLSL } from "./kit.js";

// Simulation grid. Aspect ~matches the hero canvas (608/460). Run at high res
// so structure is fine-grained, then bicubic-upscaled on display so there are
// no visible texels. The neighborhood loop is the expensive part.
const SIM_W = 720;
const SIM_H = 540;

// Seed the field with soft value-noise blobs (~organism scale) so structure
// has something to organize out of rather than uniform static.
const SEED_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform float uSeed;
  ${GLSL.noise}
  void main() {
    float n = vnoise(vUv * 14.0 + uSeed);
    n += 0.5 * vnoise(vUv * 28.0 + uSeed * 1.7);
    n /= 1.5;
    float s = smoothstep(0.46, 0.62, n);
    gl_FragColor = vec4(vec3(s), 1.0);
  }
`;

// One SmoothLife step. Rafler "SmoothTimestepRules": linearized birth/death
// intervals, hard alive/dead split on the inner disk, and euler integration
// toward the transition target — this is what makes the field evolve as smooth,
// fluid organisms that hold mid-tones (instead of snapping to 0/1).
const SIM_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uState;
  uniform vec2 uResolution;
  uniform float uDt;
  uniform float uB1;      // birth window lower
  uniform float uB2;      // birth window upper
  uniform float uD1;      // survival window lower
  uniform float uD2;      // survival window upper
  uniform float uAlphaN;  // interval softness

  const float ri = 5.0;         // inner radius
  const float ra = 15.0;        // outer radius (ra = 3 * ri is load-bearing)
  const float logres = 6.0;     // logistic kernel edge softness
  #define alphaN uAlphaN
  #define b1 uB1
  #define b2 uB2
  #define d1 uD1
  #define d2 uD2

  float linStep(float x, float x0) {
    return clamp((x - x0) / alphaN + 0.5, 0.0, 1.0);
  }
  float linInterval(float x, float a, float b) {
    return linStep(x, a) * (1.0 - linStep(x, b));
  }
  float kweight(float dist, float radius) {
    return 1.0 / (1.0 + exp(logres * (dist - radius)));
  }

  void main() {
    vec2 px = 1.0 / uResolution;
    float m = 0.0, mW = 0.0; // inner disk: weighted sum, total weight
    float n = 0.0, nW = 0.0; // outer annulus

    for (int dy = -16; dy <= 16; dy++) {
      for (int dx = -16; dx <= 16; dx++) {
        float dist = length(vec2(float(dx), float(dy)));
        if (dist > ra + 2.0) continue; // skip the box corners (weight ~0)
        float val = texture2D(uState, vUv + vec2(float(dx), float(dy)) * px).r;
        float wi = kweight(dist, ri);
        float wa = kweight(dist, ra) - wi;
        m += val * wi; mW += wi;
        n += val * wa; nW += wa;
      }
    }
    m /= mW;
    n /= nW;

    float birth = linInterval(n, b1, b2);
    float death = linInterval(n, d1, d2);
    float alive = step(0.5, m);
    float target = mix(birth, death, alive);

    float f = texture2D(uState, vUv).r;
    float nf = clamp(f + uDt * (target - f), 0.0, 1.0);
    gl_FragColor = vec4(vec3(nf), 1.0);
  }
`;

// Map the continuous state through the shared palette, bicubic-upscaled and
// gamma-spread, fading to black where the field is empty.
const DISPLAY_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uState;
  uniform vec2 uResolution;
  ${GLSL.bicubic}
  ${GLSL.palette}
  void main() {
    float v = textureBicubic(uState, vUv, uResolution);
    v = pow(clamp(v, 0.0, 1.0), 0.7); // gamma — spread the mid-tones
    vec3 col = palette(v) * smoothstep(0.0, 0.22, v);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export function smoothlife({ renderer }) {
  const stage = createStage(renderer);
  // Toroidal world — wrap the neighborhood sampling at the edges.
  const targets = createPingPong(SIM_W, SIM_H, {
    wrapS: THREE.RepeatWrapping,
    wrapT: THREE.RepeatWrapping,
  });

  const resolution = new THREE.Vector2(SIM_W, SIM_H);
  const seedMat = stage.shader(SEED_FRAG, {
    uSeed: { value: Math.random() * 1000.0 },
  });
  // Slow evolution. dt is the main "time" knob — lower is slower & smoother.
  const LIVE_DT = 0.05;
  const simMat = stage.shader(SIM_FRAG, {
    uState: { value: null },
    uResolution: { value: resolution },
    uDt: { value: LIVE_DT },
    uB1: { value: 0.254 },
    uB2: { value: 0.312 },
    uD1: { value: 0.340 },
    uD2: { value: 0.518 },
    uAlphaN: { value: 0.028 },
  });
  const displayMat = stage.shader(DISPLAY_FRAG, {
    uState: { value: null },
    uResolution: { value: resolution },
  });

  // Seed the initial state into the read target.
  stage.pass(seedMat, targets.read);

  const step = () => {
    simMat.uniforms.uState.value = targets.read.texture;
    stage.pass(simMat, targets.write);
    targets.swap();
  };

  // Warm up at a faster dt so the field is already organized on first paint,
  // then drop to the slow live dt.
  simMat.uniforms.uDt.value = 0.15;
  for (let i = 0; i < 30; i++) step();
  simMat.uniforms.uDt.value = LIVE_DT;

  // Live-tunable parameters, surfaced by the canvas manager as a slider panel.
  // `info` is the hover text for the (i) icon; ranges match the sim's useful
  // operating window.
  const u = simMat.uniforms;
  const controls = [
    {
      label: "dt",
      min: 0.01, max: 0.3, step: 0.005,
      get: () => u.uDt.value, set: (v) => (u.uDt.value = v),
      info: "Time step. The main speed knob — lower is slower and smoother, higher is faster and more chaotic.",
    },
    {
      label: "birth ↓ (b1)",
      min: 0.0, max: 0.5, step: 0.001,
      get: () => u.uB1.value, set: (v) => (u.uB1.value = v),
      info: "Birth window lower bound. A dead cell comes alive when its outer-neighborhood fill rises above this.",
    },
    {
      label: "birth ↑ (b2)",
      min: 0.0, max: 0.5, step: 0.001,
      get: () => u.uB2.value, set: (v) => (u.uB2.value = v),
      info: "Birth window upper bound. Above this it's too crowded to be born. A narrow b1–b2 means fewer spontaneous new blobs.",
    },
    {
      label: "survive ↓ (d1)",
      min: 0.0, max: 0.7, step: 0.001,
      get: () => u.uD1.value, set: (v) => (u.uD1.value = v),
      info: "Survival floor. A live cell whose outer fill drops below this dies of loneliness. Lower it so thin edges hold together and blobs coalesce.",
    },
    {
      label: "survive ↑ (d2)",
      min: 0.0, max: 0.9, step: 0.001,
      get: () => u.uD2.value, set: (v) => (u.uD2.value = v),
      info: "Over-crowding ceiling. A live cell whose outer fill exceeds this dies. Raise it to fatten blobs; too high and the field freezes into a labyrinth.",
    },
    {
      label: "softness (αN)",
      min: 0.005, max: 0.1, step: 0.001,
      get: () => u.uAlphaN.value, set: (v) => (u.uAlphaN.value = v),
      info: "Interval softness. How gradual the birth/death thresholds are. Higher is softer, blobbier, more gradient; lower is crisp and thready.",
    },
  ];

  return {
    controls,
    render() {
      step();

      // Draw the current state to the screen through the palette.
      displayMat.uniforms.uState.value = targets.read.texture;
      stage.pass(displayMat, null);
    },
    dispose() {
      targets.dispose();
      stage.dispose();
    },
  };
}
